import { Injectable, OnDestroy } from '@angular/core';
import { SessionApiSvc } from './api.services/session.api.svc';

/**
 * Messages exchanged between tabs for application discovery
 */
enum DiscoveryMessage {
  DISCOVERY_REQUEST = 'ANY_TABS_OPEN',
  DISCOVERY_RESPONSE = 'EXISTING_TAB_ACK',
  MAIN_TAB_RESPONSE = 'MAIN_TAB_ACK'
}

/**
 * Configuration for the discovery process
 */
interface DiscoveryConfig {
  readonly channelName: string;
  readonly retryDelays: readonly number[];
}

/**
 * Service for detecting other application tabs using BroadcastChannel API.
 * Uses multiple discovery attempts to handle tab switching and timing issues.
 */
@Injectable({
  providedIn: 'root'
})
export class AppDiscoveryService implements OnDestroy {
  private readonly config: DiscoveryConfig = {
    channelName: 'app_discovery_channel',
    retryDelays: [0, 300, 800] // Immediate, after tab switch settles, before app check
  };

  private readonly channel: BroadcastChannel;
  private readonly discoveryTimeouts: number[] = [];
  private otherTabDetected = false;
  private otherMainTabDetected = false;

  constructor(private readonly sessionApiSvc: SessionApiSvc) {
    this.channel = new BroadcastChannel(this.config.channelName);
    this.initializeMessageHandler();
    this.startDiscoveryProcess();
  }

  // Checks if other application tabs are currently open
  public hasOtherTabsOpen(): boolean {
    return this.otherTabDetected;
  }

  // Checks if a tab logged in through the main route is currently open
  public hasOtherMainTabOpen(): boolean {
    return this.otherMainTabDetected;
  }

  // Performs immediate cleanup of resources
  public ngOnDestroy(): void {
    this.cleanup();
  }

  // Sets up the message handler for inter-tab communication
  private initializeMessageHandler(): void {
    this.channel.onmessage = (event: MessageEvent<string>) => {
      switch (event.data) {
        case DiscoveryMessage.DISCOVERY_RESPONSE:
          this.handleDiscoveryResponse();
          break;
        case DiscoveryMessage.DISCOVERY_REQUEST:
          this.handleDiscoveryRequest();
          break;
        case DiscoveryMessage.MAIN_TAB_RESPONSE:
          this.otherMainTabDetected = true;
          break;
      }
    };
  }

  // Handles response from existing tabs
  private handleDiscoveryResponse(): void {
    this.otherTabDetected = true;
    this.clearPendingTimeouts();
  }

  // Handles discovery request from new tabs
  private handleDiscoveryRequest(): void {
    // Respond if this tab has a session (indicating it's an active application tab)
    if (this.sessionApiSvc.sessionId) {
      this.sendMessage(DiscoveryMessage.DISCOVERY_RESPONSE);
      if (this.sessionApiSvc.isOnMainRoute) {
        this.sendMessage(DiscoveryMessage.MAIN_TAB_RESPONSE);
      }
    }
  }


  // Initiates the discovery process with multiple retry attempts
  private startDiscoveryProcess(): void {
    this.config.retryDelays.forEach((delay, index) => {
      const timeoutId = window.setTimeout(() => {
        if (!this.otherTabDetected) {
          this.sendDiscoveryRequest();
        }
        // Remove this timeout from tracking
        this.removeTimeoutId(timeoutId);
      }, delay);
      
      this.discoveryTimeouts.push(timeoutId);
    });
  }

  // Sends a discovery request message
  private sendDiscoveryRequest(): void {
    this.sendMessage(DiscoveryMessage.DISCOVERY_REQUEST);
  }

  // Safely sends a message through the broadcast channel
  private sendMessage(message: DiscoveryMessage): void {
    try {
      this.channel.postMessage(message);
    } catch (error) {
      console.warn('Failed to send discovery message:', error);
    }
  }

  // Clears all pending timeout operations
  private clearPendingTimeouts(): void {
    this.discoveryTimeouts.forEach(id => window.clearTimeout(id));
    this.discoveryTimeouts.length = 0;
  }

  // Removes a specific timeout ID from tracking
  private removeTimeoutId(timeoutId: number): void {
    const index = this.discoveryTimeouts.indexOf(timeoutId);
    if (index !== -1) {
      this.discoveryTimeouts.splice(index, 1);
    }
  }

  // Cleans up all resources used by the service
  private cleanup(): void {
    this.clearPendingTimeouts();
    this.channel.close();
  }
}
